/**
 * Nutrition Information Form Component
 * Comprehensive form for entering nutrition facts and dietary information
 */

import { Input } from './ui';
import { NutritionInformation, MacronutrientInfo } from '../../../backend/src/types/enhanced-product-info.types';

interface NutritionInformationFormProps {
  nutritionInfo: Partial<NutritionInformation>;
  onChange: (field: keyof NutritionInformation, value: any) => void;
  disabled?: boolean;
  errors: { [key: string]: string };
}

export default function NutritionInformationForm({
  nutritionInfo,
  onChange,
  disabled = false,
  errors
}: NutritionInformationFormProps) {
  
  const handleMacronutrientChange = (field: keyof MacronutrientInfo, value: number | undefined) => {
    const currentMacros = nutritionInfo.macronutrients || {};
    onChange('macronutrients', {
      ...currentMacros,
      [field]: value
    });
  };

  return (
    <div>
      <h4 className="text-md font-medium text-gray-900 mb-4">Nutrition Information</h4>
      <div className="space-y-4">
        {/* Basic Serving Information */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Serving Size *
            </label>
            <Input
              value={nutritionInfo.servingSize || ''}
              onChange={(e) => onChange('servingSize', e.target.value)}
              placeholder="e.g., 1 cup (240ml)"
              disabled={disabled}
              error={errors.servingSize}
            />
            {errors.servingSize && (
              <p className="mt-1 text-sm text-red-600">{errors.servingSize}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Servings Per Container
            </label>
            <Input
              type="number"
              min="1"
              step="0.1"
              value={nutritionInfo.servingsPerContainer || ''}
              onChange={(e) => onChange('servingsPerContainer', parseFloat(e.target.value) || undefined)}
              placeholder="e.g., 2.5"
              disabled={disabled}
            />
          </div>
        </div>

        {/* Calories */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Calories Per Serving *
            </label>
            <Input
              type="number"
              min="0"
              max="9999"
              value={nutritionInfo.caloriesPerServing || ''}
              onChange={(e) => onChange('caloriesPerServing', parseFloat(e.target.value) || 0)}
              placeholder="e.g., 150"
              disabled={disabled}
              error={errors.caloriesPerServing}
            />
            {errors.caloriesPerServing && (
              <p className="mt-1 text-sm text-red-600">{errors.caloriesPerServing}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Calories Per 100g/100ml
            </label>
            <Input
              type="number"
              min="0"
              max="9999"
              value={nutritionInfo.caloriesPer100g || ''}
              onChange={(e) => onChange('caloriesPer100g', parseFloat(e.target.value) || undefined)}
              placeholder="e.g., 62"
              disabled={disabled}
            />
          </div>
        </div>

        {/* Macronutrients */}
        <div>
          <h5 className="text-sm font-medium text-gray-800 mb-3">Macronutrients (per serving)</h5>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Protein (g) *
              </label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={nutritionInfo.macronutrients?.protein || ''}
                onChange={(e) => handleMacronutrientChange('protein', parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={disabled}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Carbohydrates (g) *
              </label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={nutritionInfo.macronutrients?.carbohydrates || ''}
                onChange={(e) => handleMacronutrientChange('carbohydrates', parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={disabled}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Total Fat (g) *
              </label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={nutritionInfo.macronutrients?.totalFat || ''}
                onChange={(e) => handleMacronutrientChange('totalFat', parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={disabled}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Sodium (mg) *
              </label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={nutritionInfo.macronutrients?.sodium || ''}
                onChange={(e) => handleMacronutrientChange('sodium', parseFloat(e.target.value) || 0)}
                placeholder="0"
                disabled={disabled}
              />
            </div>
          </div>

          {/* Optional Macronutrients */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Saturated Fat (g)
              </label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={nutritionInfo.macronutrients?.saturatedFat || ''}
                onChange={(e) => handleMacronutrientChange('saturatedFat', parseFloat(e.target.value) || undefined)}
                placeholder="0"
                disabled={disabled}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Trans Fat (g)
              </label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={nutritionInfo.macronutrients?.transFat || ''}
                onChange={(e) => handleMacronutrientChange('transFat', parseFloat(e.target.value) || undefined)}
                placeholder="0"
                disabled={disabled}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Fiber (g)
              </label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={nutritionInfo.macronutrients?.fiber || ''}
                onChange={(e) => handleMacronutrientChange('fiber', parseFloat(e.target.value) || undefined)}
                placeholder="0"
                disabled={disabled}
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Sugar (g)
              </label>
              <Input
                type="number"
                min="0"
                step="0.1"
                value={nutritionInfo.macronutrients?.sugar || ''}
                onChange={(e) => handleMacronutrientChange('sugar', parseFloat(e.target.value) || undefined)}
                placeholder="0"
                disabled={disabled}
              />
            </div>
          </div>
        </div>

        {/* Helper Text */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p className="text-xs text-blue-800">
            <strong>Tip:</strong> Enter nutrition values per serving. Required fields are marked with *. 
            All values should be based on the serving size specified above.
          </p>
        </div>
      </div>
    </div>
  );
}
